import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Seo from '../components/Seo';
import { Award, Leaf, Shield, HeadphonesIcon, Heart, Users, CheckCircle, Wrench, Eye, ChevronRight, ChevronLeft, Flag } from 'lucide-react';
import { Link } from 'react-router-dom';

// About Us Gallery Images
const ABOUT_GALLERY_IMAGES = [
  {
    src: 'https://customer-assets.emergentagent.com/job_967bcab9-dfc5-4b16-a75b-f4c7de47dc0b/artifacts/t621ubd3_unnamed.png',
    alt: 'Leisure Wellness Naples Showroom - Pool Products & Expert Staff'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_967bcab9-dfc5-4b16-a75b-f4c7de47dc0b/artifacts/kvmv7ww6_unnamed-1.png',
    alt: 'Leisure Wellness Naples - Hot Tub Consultation with Customers'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_967bcab9-dfc5-4b16-a75b-f4c7de47dc0b/artifacts/zu8htqkn_unnamed-2.png',
    alt: 'Leisure Wellness Naples - Premium Spa Showroom Experience'
  }
];

const AboutPage = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % ABOUT_GALLERY_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % ABOUT_GALLERY_IMAGES.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + ABOUT_GALLERY_IMAGES.length) % ABOUT_GALLERY_IMAGES.length);
  };

  return (
    <>
      <Seo page="/about" />
      
      <div className="pt-36 pb-20" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #E0FFFF 20%, #AFEEEE 50%, #98FB98 80%, #F0FFF0 100%)' }} data-testid="about-page">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* About Us Image Gallery */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12 relative">
            <div className="relative h-[400px] md:h-[500px] overflow-hidden shadow-xl">
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentImageIndex}
                  src={ABOUT_GALLERY_IMAGES[currentImageIndex].src}
                  alt={ABOUT_GALLERY_IMAGES[currentImageIndex].alt}
                  className="w-full h-full object-cover"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                />
              </AnimatePresence>
              
              {/* Navigation Arrows */}
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-3 rounded-full shadow-lg transition-all"
                aria-label="Previous image"
              >
                <ChevronLeft className="text-[#0E3D95]" size={24} />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-3 rounded-full shadow-lg transition-all"
                aria-label="Next image"
              >
                <ChevronRight className="text-[#0E3D95]" size={24} />
              </button>
              
              {/* Dots Indicator */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {ABOUT_GALLERY_IMAGES.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      idx === currentImageIndex ? 'bg-[#F88200] w-8' : 'bg-white/70 hover:bg-white'
                    }`}
                    aria-label={`Go to image ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2 mb-4">
            <Heart className="text-[#F88200]" size={24} />
            <span className="text-sm font-semibold text-[#F88200] uppercase tracking-wider">Family Owned & Customer Focused</span>
          </motion.div>
          
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-['Barlow_Condensed'] text-4xl md:text-6xl font-bold uppercase text-[#0E3D95] mb-6">
            About Us
          </motion.h1>
          
          {/* Main About Us Content */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white p-8 md:p-12 shadow-lg mb-12"
          >
            <p className="text-xl md:text-2xl text-slate-700 leading-relaxed mb-6">
              We are a <span className="font-bold text-[#0E3D95]">family-owned business</span> built on honesty, education, and real customer care. We don't sell to you—we help you find the right wellness package for your lifestyle and needs.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              There's never high-pressure sales, hidden fees, or surprises. Our <span className="font-semibold text-[#F88200]">all-inclusive pricing is transparent</span> from the start, and we take the time to answer your questions so you can decide with confidence.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              We're proud to offer <span className="font-semibold text-[#0E3D95]">American-made products</span> and even prouder of the long-term relationships we build with our customers. From your first visit to delivery—and long after—our team is always here to support you.
            </p>
            <p className="text-xl font-semibold text-[#0E3D95]">
              Stop in, compare the difference, and experience a wellness company that listens, educates, and truly cares.
            </p>
          </motion.div>
          
          {/* Key Values */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              { 
                title: 'Honest & Transparent', 
                icon: CheckCircle,
                text: 'No hidden fees, no surprises. Our all-inclusive pricing is transparent from the start so you can make informed decisions.' 
              },
              { 
                title: 'Education First', 
                icon: Users,
                text: "We take the time to answer your questions and help you find the right wellness package for your lifestyle and needs." 
              },
              { 
                title: 'American Made', 
                icon: null,
                emoji: '🇺🇸',
                text: "We're proud to offer American-made products with quality craftsmanship and outstanding warranties you can count on." 
              },
            ].map((item, idx) => (
              <motion.div 
                key={item.title} 
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                viewport={{ once: true }} 
                transition={{ delay: idx * 0.1 }} 
                className="bg-white p-8 shadow-lg text-center"
              >
                <div className="w-16 h-16 bg-[#F88200]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  {item.emoji ? (
                    <span className="text-3xl">{item.emoji}</span>
                  ) : (
                    <item.icon className="text-[#F88200]" size={32} />
                  )}
                </div>
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-[#0E3D95] mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.text}</p>
              </motion.div>
            ))}
          </div>
          
          {/* Helpful Tools & Resources Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Wrench className="text-[#F88200]" size={24} />
                <span className="text-sm font-semibold text-[#F88200] uppercase tracking-wider">Plan Your Purchase</span>
              </div>
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-[#0E3D95] mb-4">
                Helpful Tools & Resources
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Use these helpful tools to visualize your new hot tub and explore accessory options before you visit our showroom.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Link 
                to="/ar-visualizer"
                className="bg-white p-8 shadow-lg hover:shadow-xl transition-all group flex items-start gap-4"
              >
                <div className="w-16 h-16 bg-[#0E3D95]/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#F88200] transition-colors">
                  <Eye className="text-[#0E3D95] group-hover:text-white transition-colors" size={28} />
                </div>
                <div>
                  <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-[#0E3D95] mb-2 group-hover:text-[#F88200] transition-colors">
                    See It In Your Backyard
                  </h3>
                  <p className="text-slate-600 mb-3">
                    Use our AR Visualizer to see how a hot tub would look in your own backyard space before you buy.
                  </p>
                  <span className="inline-flex items-center gap-2 text-[#F88200] font-semibold">
                    Try AR Visualizer <ChevronRight size={18} />
                  </span>
                </div>
              </Link>
              
              <Link 
                to="/covers"
                className="bg-white p-8 shadow-lg hover:shadow-xl transition-all group flex items-start gap-4"
              >
                <div className="w-16 h-16 bg-[#0E3D95]/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#F88200] transition-colors">
                  <Shield className="text-[#0E3D95] group-hover:text-white transition-colors" size={28} />
                </div>
                <div>
                  <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-[#0E3D95] mb-2 group-hover:text-[#F88200] transition-colors">
                    Hot Tub Covers
                  </h3>
                  <p className="text-slate-600 mb-3">
                    Explore our premium cover options to protect your investment and maintain water temperature efficiently.
                  </p>
                  <span className="inline-flex items-center gap-2 text-[#F88200] font-semibold">
                    View Cover Options <ChevronRight size={18} />
                  </span>
                </div>
              </Link>
            </div>
          </motion.div>
          
          {/* Leisure Wellness Naples Video */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="mb-16"
          >
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold text-[#0E3D95] mb-6">See Leisure Wellness Naples in Action</h2>
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full shadow-xl"
                src="https://www.youtube.com/embed/aRDW_vz1bUw"
                title="Leisure Wellness Naples Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </motion.div>
          
          {/* Why American Made Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">🇺🇸</span>
              <span className="text-sm font-semibold text-[#F88200] uppercase tracking-wider">American Made & Proud of It</span>
            </div>
            <h2 className="font-['Barlow_Condensed'] text-3xl font-bold text-[#0E3D95] mb-8">Why We Choose American Made Products</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {[
                { 
                  title: 'American Jobs', 
                  icon: null,
                  emoji: '🇺🇸',
                  text: 'The loss of American family owned manufacturing jobs over the past 15 years has had a staggering effect on the United States economy and job market. Our American made spas and swim spas products further ensure that Leisure Wellness Naples will keep their manufacturing efforts in the United States and employ American workers.' 
                },
                { 
                  title: 'Environmental Concerns', 
                  icon: Leaf,
                  text: 'Purchasing made in America Spas and Swim Spas just makes environmental sense. American-Made Spas and Swim Spas carry a much smaller carbon footprint than products overseas. Shipping products around the globe comes at a high environmental cost due to plane and ship emissions.' 
                },
                { 
                  title: 'Quality', 
                  icon: Shield,
                  text: 'When you see "Made In The USA" sticker on your spa, you know that you have your hands on good quality American made spa. Leisure Wellness Naples manufactures the spas and swim spas in the U.S. and has full control of the process from the materials being used to the way products are being constructed.' 
                },
                { 
                  title: 'Factory Service', 
                  icon: HeadphonesIcon,
                  text: 'Leisure Wellness Naples factory service starts by everyone at Leisure Wellness Naples treating others like they would like to be treated. Returning calls on the same day or next morning. Following up to make certain our customers are taken care of. Getting orders to customers as quickly as possible without quality compromise.' 
                },
              ].map((item, idx) => (
                <motion.div 
                  key={item.title} 
                  initial={{ opacity: 0, y: 20 }} 
                  whileInView={{ opacity: 1, y: 0 }} 
                  viewport={{ once: true }} 
                  transition={{ delay: idx * 0.1 }} 
                  className="bg-slate-50 p-8"
                >
                  <div className="flex items-center gap-3 mb-4">
                    {item.emoji ? (
                      <span className="text-3xl">{item.emoji}</span>
                    ) : (
                      <item.icon className="text-[#F88200]" size={28} />
                    )}
                    <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-[#0E3D95]">{item.title}</h3>
                  </div>
                  <p className="text-slate-600">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
          
          {/* 4 Seasons of Benefits */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#0E3D95] text-white p-8 md:p-12 mb-16"
          >
            <h2 className="font-['Barlow_Condensed'] text-3xl font-bold mb-6">Leisure Wellness Naples 4 Seasons Of Benefits</h2>
            <p className="text-slate-300 mb-6">
              Taking a spa is enjoyable all year round, but it's even more so in the winter. There is nothing better than enjoying warm and relaxing water to forget the inconveniences of the cold season and appreciate its beauty. The special insulation of Leisure Wellness Naples allows the water to remain at a high temperature even in the coldest weather.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <ul className="space-y-2 text-slate-300">
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37]">•</span>
                  Heat-Shield RMAX insulation system
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37]">•</span>
                  Multi-Layered fiberglass spa shell
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37]">•</span>
                  Foil faced RMAX Foam Panels
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37]">•</span>
                  Maintenance Free Confer Skirting
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37]">•</span>
                  ABS Permanent Bottom
                </li>
              </ul>
              <ul className="space-y-2 text-slate-300">
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37]">•</span>
                  Floor entirely covered with heat reflecting bubble insulation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37]">•</span>
                  Heated Airspace
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37]">•</span>
                  Up to 7 times the R-Value of competing models
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37]">•</span>
                  Acts as a sound barrier
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37]">•</span>
                  Additional protection from freezing
                </li>
              </ul>
            </div>
          </motion.div>
          
          {/* Hot Tub Benefits Video */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <h2 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold text-[#0E3D95] mb-4">
              Hot Tub Benefits
            </h2>
            <p className="text-lg text-slate-600 mb-6">
              Discover the many health and wellness benefits of owning a hot tub from Leisure Wellness Naples.
            </p>
            <div className="max-w-4xl aspect-video rounded-lg overflow-hidden shadow-2xl">
              <video 
                autoPlay 
                muted 
                loop 
                playsInline 
                controls
                className="w-full h-full object-cover"
              >
                <source src="https://customer-assets-jt897jd0.emergentagent.net/job_product-pricing-27/artifacts/0eqytheo_Leisure%20Wellness%20Naples%20%E2%80%94%20Play%20Hard%2C%20Recover%20Harder-2.mp4" type="video/mp4" />
              </video>
            </div>
          </motion.div>
          
          {/* M.A.E. Certified Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-slate-100 p-8 md:p-12 mb-16"
          >
            <div className="flex items-center gap-3 mb-6">
              <Award className="text-[#D4AF37]" size={36} />
              <h2 className="font-['Barlow_Condensed'] text-3xl font-bold text-[#0E3D95]">M.A.E. CERTIFIED</h2>
            </div>
            <p className="text-slate-600 mb-6">
              At Leisure Wellness Naples, we take pride in our products. We ensure that our spas are aesthetically pleasing, have sturdy construction and are energy efficient for the end user.
            </p>
            <p className="text-slate-600 mb-6">
              The Leisure Wellness Naples Modernized Appliance Efficiency Laboratory (M.A.E. Lab) is where we apply rigorous test procedures to each of our models. With high precision instruments, we verify that our spas not only maintain a consistent temperature, but also meet the energy requirements stated by the ANSI/APSP 2019 standards, as well as California Title 20.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-[#0E3D95] mb-3">Testing Process:</h4>
                <ul className="space-y-2 text-slate-600 text-sm">
                  <li>• Verified for fit and finish</li>
                  <li>• Water volume recorded</li>
                  <li>• Heated to specified temperature</li>
                  <li>• Stabilization test (minimum 4 hours)</li>
                  <li>• Official energy test (72 hours)</li>
                  <li>• Data uploaded to MAEDBS database</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-[#0E3D95] mb-3">Lab Specifications:</h4>
                <ul className="space-y-2 text-slate-600 text-sm">
                  <li>• Fully Insulated and Temperature controlled</li>
                  <li>• ISO 17025 compliant</li>
                  <li>• High precision calibrated instruments</li>
                  <li>• Meets ANSI/APSP testing specifications</li>
                  <li>• Meets California Title 20 Specification</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* How Hot Tubs Are Made Video Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="text-center mb-8">
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-[#0E3D95] mb-4">
                How Hot Tubs Are Made
              </h2>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                See the craftsmanship and attention to detail that goes into building every American-made hot tub.
              </p>
            </div>
            <div className="max-w-4xl mx-auto">
              <div className="relative aspect-video rounded-lg overflow-hidden shadow-2xl">
                <video 
                  controls 
                  className="w-full h-full object-contain bg-black"
                >
                  <source src="/videos/how-tubs-are-made.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </motion.div>
          
          {/* CTA Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#0E3D95] p-8 text-center"
          >
            <p className="text-white text-xl md:text-2xl font-['Barlow_Condensed'] uppercase tracking-wider mb-4">
              Ready to Start Your Wellness Journey?
            </p>
            <p className="text-slate-300 mb-6">
              Stop in, compare the difference, and experience a wellness company that listens, educates, and truly cares.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2 text-lg px-8 py-4">
                Contact Us Today
              </Link>
              <Link to="/hot-tubs" className="bg-white text-[#0E3D95] font-bold py-4 px-8 hover:bg-slate-100 transition-colors">
                Browse Products
              </Link>
            </div>
            <p className="text-[#D4AF37] font-semibold flex items-center justify-center gap-2 mt-6">
              <Flag size={16} /> American Made & Proud of It
            </p>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default AboutPage;
